import { Banner, Button, Code, Dialog } from '@neo4j-ndl/react';
import { CheckIconOutline, Square2StackIconOutline } from '@neo4j-ndl/react/icons';
import { APP_SCOPE, QUERY_TYPE } from '@nx/constants';
import { useCypherQuery, useDeferredCypherQuery } from '@nx/state';
import { isNotNullish, isNullish } from '@nx/stdlib';
import { useCopyWithMessage } from '@nx/ui';
import { useEffect, useMemo, useState } from 'react';

export enum ENTITY_TYPE {
  NODE = 'NODE',
  RELATIONSHIP = 'RELATIONSHIP',
}

export interface IndexToFix {
  name: string;
  state: string;
  type: string;
  entityType: ENTITY_TYPE;
  labelsOrTypes: string[];
  properties: string[];
  constraintBacking: boolean;
  replacementName?: string;
  replacementQueryFinished: boolean;
  replacementQueryError: boolean;
}

function getFixIndexQuery(oldIndex: IndexToFix, replacementName: string) {
  const getForPattern = (entityType: ENTITY_TYPE, labelOrTypes: string[]) => {
    switch (entityType) {
      case ENTITY_TYPE.NODE:
        return labelOrTypes.map((label) => `n:${label}`).join('|');
      case ENTITY_TYPE.RELATIONSHIP:
        return `()-[${labelOrTypes.map((label) => `r:${label}`).join('|')}]-()`;
      default:
        throw new Error('Unknown entity type');
    }
  };

  const getOnPattern = (entityType: ENTITY_TYPE, properties: string[]) => {
    const alias = entityType === ENTITY_TYPE.NODE ? 'n' : 'r';
    return `${properties.map((property) => `${alias}.${property}`).join(', ')}`;
  };

  const forPattern = getForPattern(oldIndex.entityType, oldIndex.labelsOrTypes);
  const onPattern = getOnPattern(oldIndex.entityType, oldIndex.properties);

  return `CREATE RANGE INDEX ${replacementName} FOR (${forPattern}) ON (${onPattern});`;
}

interface FixIndexQueryProps {
  createIndexQuery: string;
  replacementName: string;
  start: boolean;
  onFinished: (success: boolean) => void;
}

function FixIndexQuery({ createIndexQuery, replacementName, start, onFinished }: FixIndexQueryProps) {
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [success, setSuccess] = useState<boolean>(false);
  const [started, setStarted] = useState<boolean>(false);
  const [, fixIndexCypherQuery] = useDeferredCypherQuery(
    {
      query: createIndexQuery,
    },
    {
      metadata: { appScope: APP_SCOPE.ops, queryType: QUERY_TYPE.UserAction },
      sessionConfig: { database: 'neo4j' },
      transactionMode: 'write',
    },
  );

  useEffect(() => {
    if (start && !started) {
      setStarted(true);
      const resultPromise = fixIndexCypherQuery({});
      resultPromise
        .unwrap()
        .then(() => {
          setSuccess(true);
          onFinished(true);
        })
        .catch((rawError: Error) => {
          setErrorMessage(rawError.message);
          onFinished(false);
        });
    }
  }, [start, fixIndexCypherQuery, onFinished, started]);

  return (
    <>
      {isNotNullish(errorMessage) && <Banner description={errorMessage} type="danger" hasIcon usage="inline" />}
      {success && (
        <Banner description={`Index ${replacementName} created successfully.`} type="success" hasIcon usage="inline" />
      )}
    </>
  );
}

interface FixIndexModalProps {
  oldIndexes: IndexToFix[];
  isOpen: boolean;
  onClose: () => void;
}

export const FixIndexesModal = ({ oldIndexes, isOpen, onClose }: FixIndexModalProps) => {
  const [allIndexNamesQuery, invalidateAllIndexNamesQuery] = useCypherQuery<{ name: string }>(
    { query: 'SHOW INDEXES YIELD name' },
    {
      metadata: { appScope: APP_SCOPE.ops, queryType: QUERY_TYPE.UserAction },
      sessionConfig: { database: 'neo4j' },
      transactionMode: 'read',
    },
  );

  function findFreeIndexName(oldIndex: IndexToFix, allIndexNames: string[]) {
    let freeIndexName = `${oldIndex.name}_replacement`;
    let i = 1;
    while (allIndexNames.includes(freeIndexName)) {
      freeIndexName = `${oldIndex.name}_replacement_${i}`;
      i += 1;
    }
    return freeIndexName;
  }

  useEffect(() => {
    if (isOpen) {
      invalidateAllIndexNamesQuery();
    }
  }, [isOpen, invalidateAllIndexNamesQuery]);

  const allIndexNames = useMemo(() => {
    if (allIndexNamesQuery.fetching || isNullish(allIndexNamesQuery.records)) {
      return [];
    }
    return allIndexNamesQuery.records.map((record) => record.get('name'));
  }, [allIndexNamesQuery.fetching, allIndexNamesQuery.records]);

  const [query] = useMemo(() => {
    if (allIndexNamesQuery.fetching || isNullish(allIndexNamesQuery.records)) {
      return [null];
    }
    const queries = oldIndexes
      .filter((index) => isNullish(index.replacementName))
      .map((index) => getFixIndexQuery(index, findFreeIndexName(index, allIndexNames)));

    return [queries.join('\n')];
  }, [allIndexNamesQuery.fetching, allIndexNamesQuery.records, oldIndexes, allIndexNames]);

  const [copyMessage, copyWithFeedback] = useCopyWithMessage();

  const toFix = oldIndexes.filter((index) => isNullish(index.replacementName));
  const [start, setStart] = useState(false);

  return (
    <Dialog
      size="large"
      isOpen={isOpen}
      onClose={onClose}
      modalProps={{
        className: '!max-h-[785px] relative',
      }}
    >
      <Dialog.Header>Create replacement indexes</Dialog.Header>
      <Dialog.Content>
        {isNotNullish(query) && isNotNullish(allIndexNames) && (
          <div className="flex flex-col gap-3">
            <Code
              headerTitle="Cypher query"
              language="cypher"
              code={query}
              actions={[
                {
                  ariaLabel: 'Copy to clipboard',
                  htmlAttributes: { title: 'Copy to clipboard' },
                  size: 'small',
                  children: /copied/i.test(copyMessage ?? '') ? <CheckIconOutline /> : <Square2StackIconOutline />,
                  onClick: () => {
                    void copyWithFeedback(query);
                  },
                },
              ]}
            />
            Index replacement created by the above statement will be of type RANGE. In cases where RANGE is not the best
            option, change the provided Cypher query accordingly (e.g. to create TEXT or POINT index) and execute it
            manually.
            <Button
              isLoading={start && toFix.some((index) => !index.replacementQueryFinished)}
              isDisabled={start}
              onClick={() => setStart(true)}
            >
              Create replacement index(es)
            </Button>
            {oldIndexes
              .filter((index) => isNullish(index.replacementName))
              .sort((a, b) => {
                if (a.replacementQueryError && !b.replacementQueryError) {
                  return -1;
                }
                return 1;
              })
              .map((index) => {
                const freeIndexName = findFreeIndexName(index, allIndexNames);
                return (
                  <FixIndexQuery
                    key={index.name}
                    createIndexQuery={getFixIndexQuery(index, freeIndexName)}
                    replacementName={freeIndexName}
                    start={start}
                    onFinished={(success) => {
                      index.replacementQueryFinished = true;
                      index.replacementQueryError = !success;
                    }}
                  />
                );
              })}
          </div>
        )}
      </Dialog.Content>
    </Dialog>
  );
};
